import axios from "axios";
import { getExpectedUserType } from "../utils/authScope";
import { getOrCreateDeviceId } from "../utils/deviceId";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5259/api",
  // Without a timeout a stalled request (e.g. the API container cold-starting) leaves the page on
  // skeletons forever instead of failing into the catch handlers.
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-Platform": "web",
  },
});

axiosClient.interceptors.request.use((config) => {
  // Same per-browser id the guest login uses, so the backend can tie anonymous events, page views
  // and the visitor profile to one device (mirrors the mobile app's X-Device-Id header).
  config.headers["X-Device-Id"] = getOrCreateDeviceId();

  let token = null;
  let rawAuth = null;
  try {
    token = localStorage.getItem("bigmarket_token");
    rawAuth = localStorage.getItem("bigmarket_auth");
  } catch {
    // storage can be blocked in some in-app browsers; send the request unauthenticated
  }

  if (token && rawAuth) {
    try {
      const { userType } = JSON.parse(rawAuth);
      // Only send the stored session's token on requests made from its own role
      // section - otherwise a session logged in elsewhere (e.g. business owner)
      // would get attached to requests made while browsing another role's pages.
      if (userType === getExpectedUserType(window.location.pathname)) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // ignore malformed stored auth
    }
  }

  return config;
});

// Pages catch API errors and fall back to empty lists, which hides the real cause. Log every
// failure (and show it in the temporary debug overlay from public/index.html, if present).
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const { config = {}, response } = error;
    const message = `API ${String(config.method || "").toUpperCase()} ${config.baseURL || ""}${config.url || ""} failed: ${
      response ? `HTTP ${response.status}` : `${error.code || "network error"} - ${error.message}`
    }`;
    console.error(message, error);
    if (typeof window.__showDebugError === "function") window.__showDebugError(message);
    return Promise.reject(error);
  },
);

export default axiosClient;
